import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthPanel, useFrom, useServerCodeToast } from '@/components/auth';
import { useCurrentUserContext } from '@/contexts';
import { useCampaignTracking } from '@/hooks/useCampaignTracking';
import { useLauncherAction } from '@/hooks/useLauncher';
import { useMetaThemeColor } from '@/hooks/useMetaThemeColor';
import { Flex, Heading, Image } from '@/ui';
import { isDatesNewDestination } from './datesNewDestination';
import { DatesSplit } from './DatesSplit';
import { isPlannerCheckoutDestination } from './plannerCheckoutDestination';

type HeroTextProps = {
  destination: string | undefined;
};

const HeroText = ({ destination }: HeroTextProps) => {
  if (isPlannerCheckoutDestination(destination)) {
    return 'Log in or sign up to get your planner!';
  }

  return 'Log in or create an account';
};

export const Auth = () => {
  const from = useFrom();
  const [defaults, setDefaults] = useState({});
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useCurrentUserContext();
  useServerCodeToast();
  useCampaignTracking();

  useLauncherAction('signup', setDefaults);
  useMetaThemeColor('white');

  // Set once the panel leaves its opening step, which happens when the user
  // submits an email - always at least one commit before a session can exist.
  // A ref rather than state: the redirect effect below has to see it in the same
  // commit it is set, and a child's setState would not have landed by then.
  const authStartedHere = useRef(false);

  // Only bounces someone who was already signed in when they arrived. A session
  // started here belongs to the panel, which may still have a step left to run:
  // authenticating seeds the profile cache before the panel puts up its name
  // prompt, so redirecting on `isAuthenticated` alone tore that prompt away a
  // frame after it rendered. The panel's own onAuthenticated does that redirect
  // once it is genuinely finished.
  //
  // Keyed off the panel rather than a snapshot of the first settled profile
  // value: seeding the cache also resolves a still-pending profile query, so a
  // slow one would make a brand new session look like it arrived with us.
  useEffect(() => {
    if (isLoading || !isAuthenticated || !from || authStartedHere.current) {
      return;
    }
    if (from.isServerRoute) {
      window.location.href = from.destination;
      return;
    }
    navigate(from.destination, { replace: true });
  }, [isLoading, isAuthenticated, from, navigate]);

  // Mutually exclusive: a destination is either the /new endpoint or a checkout.
  const isPlannerCheckout = isPlannerCheckoutDestination(from?.destination);
  const isDatesNew = isDatesNewDestination(from?.destination);

  const authPanel = (
    <AuthPanel
      defaults={defaults}
      redirectTo={from?.destination}
      renderHeader={() => (
        <Heading
          as="h2"
          fontSize={{ base: '2xl', sm: '4xl' }}
          fontWeight="bold"
          mb="7"
          textAlign="center"
        >
          <HeroText destination={from?.destination} />
        </Heading>
      )}
      onAuthenticated={(result, data) => {
        // Only the legacy password registration returns a seeded schedule;
        // code signups have no equivalent, so they fall through to /dashboard.
        const welcomeScheduleId =
          data && 'register' in data && data.register.schedules[0]?.id;

        let redirectTo = '/dashboard';
        if (from) {
          if (from.isServerRoute) {
            window.location.href = from.destination;
            return;
          }
          redirectTo = from.destination;
        } else if (welcomeScheduleId) {
          redirectTo = `/schedule/${welcomeScheduleId}`;
        }

        navigate(redirectTo, { replace: true });
      }}
      onStepChange={(step) => {
        if (step !== null) {
          authStartedHere.current = true;
        }
      }}
    />
  );

  if (isDatesNew) {
    return <DatesSplit>{authPanel}</DatesSplit>;
  }

  return (
    <Flex
      align={{ base: 'flex-start', md: 'center' }}
      direction={{ base: 'column', md: 'row' }}
      gap={{ base: 0, md: '32' }}
      justify="center"
      w="100%"
    >
      {isPlannerCheckout && (
        <Image
          alt="Planner preview"
          borderRadius="md"
          display={{ base: 'none', lg: 'block' }}
          flexShrink={0}
          src="/planner-preview.png"
          w={{ md: '380px', lg: '440px' }}
        />
      )}
      {authPanel}
    </Flex>
  );
};
